const BIOMETRIC_ENABLED_PREFIX = 'pin_biometric_enabled'
const BIOMETRIC_CREDENTIAL_PREFIX = 'pin_biometric_credential_id'
const BIOMETRIC_PIN_PREFIX = 'pin_biometric_stored_code'
const BIOMETRIC_STORAGE_PREFIX = 'pin_biometric_'

function encodeBase64Url(bytes: Uint8Array) {
  let binary = ''

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes
}

function getEnabledKey(userId: string) {
  return `${BIOMETRIC_ENABLED_PREFIX}_${userId}`
}

function getCredentialKey(userId: string) {
  return `${BIOMETRIC_CREDENTIAL_PREFIX}_${userId}`
}

function getStoredPinKey(userId: string) {
  return `${BIOMETRIC_PIN_PREFIX}_${userId}`
}

function migrateLegacyKeys(userId: string) {
  const legacyEnabled = localStorage.getItem(BIOMETRIC_ENABLED_PREFIX)
  const legacyCredential = localStorage.getItem(BIOMETRIC_CREDENTIAL_PREFIX)

  if (legacyEnabled === '1' && !localStorage.getItem(getEnabledKey(userId))) {
    localStorage.setItem(getEnabledKey(userId), '1')
  }

  if (legacyCredential && !localStorage.getItem(getCredentialKey(userId))) {
    localStorage.setItem(getCredentialKey(userId), legacyCredential)
  }

  localStorage.removeItem(BIOMETRIC_ENABLED_PREFIX)
  localStorage.removeItem(BIOMETRIC_CREDENTIAL_PREFIX)
}

export function usePinBiometric() {
  function isSupported() {
    return import.meta.client
      && typeof window.PublicKeyCredential !== 'undefined'
      && typeof navigator.credentials !== 'undefined'
  }

  async function isPlatformAuthenticatorAvailable() {
    if (!isSupported()) {
      return false
    }

    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    }
    catch {
      return false
    }
  }

  function isEnabled(userId?: string) {
    if (!import.meta.client || !userId) {
      return false
    }

    migrateLegacyKeys(userId)

    return localStorage.getItem(getEnabledKey(userId)) === '1'
  }

  async function enroll(userId: string, pin: string) {
    if (!isSupported() || !userId) {
      return false
    }

    const challenge = crypto.getRandomValues(new Uint8Array(32))
    const userIdBytes = new TextEncoder().encode(userId)

    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: 'Квест',
          id: window.location.hostname,
        },
        user: {
          id: userIdBytes,
          name: userId,
          displayName: userId,
        },
        pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
        },
        timeout: 60000,
      },
    }) as PublicKeyCredential | null

    if (!credential) {
      return false
    }

    const credentialId = encodeBase64Url(new Uint8Array(credential.rawId))
    localStorage.setItem(getCredentialKey(userId), credentialId)
    localStorage.setItem(getEnabledKey(userId), '1')
    localStorage.setItem(getStoredPinKey(userId), pin)
    localStorage.removeItem(BIOMETRIC_ENABLED_PREFIX)
    localStorage.removeItem(BIOMETRIC_CREDENTIAL_PREFIX)

    return true
  }

  async function authenticate(userId: string) {
    if (!isSupported() || !userId || !isEnabled(userId)) {
      return null
    }

    const credentialId = localStorage.getItem(getCredentialKey(userId))

    if (!credentialId) {
      return null
    }

    const challenge = crypto.getRandomValues(new Uint8Array(32))

    try {
      await navigator.credentials.get({
        publicKey: {
          challenge,
          allowCredentials: [{
            id: decodeBase64Url(credentialId),
            type: 'public-key',
          }],
          userVerification: 'required',
          timeout: 60000,
        },
      })

      return localStorage.getItem(getStoredPinKey(userId))
    }
    catch {
      return null
    }
  }

  function disable(userId?: string) {
    if (!import.meta.client) {
      return
    }

    if (!userId) {
      disableAll()
      return
    }

    localStorage.removeItem(getEnabledKey(userId))
    localStorage.removeItem(getCredentialKey(userId))
    localStorage.removeItem(getStoredPinKey(userId))
    localStorage.removeItem(BIOMETRIC_ENABLED_PREFIX)
    localStorage.removeItem(BIOMETRIC_CREDENTIAL_PREFIX)
  }

  function disableAll() {
    if (!import.meta.client) {
      return
    }

    const keysToRemove: string[] = []

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)

      if (key?.startsWith(BIOMETRIC_STORAGE_PREFIX)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key)
    })
  }

  function updateStoredPin(userId: string, pin: string) {
    if (!import.meta.client || !userId || !isEnabled(userId)) {
      return
    }

    localStorage.setItem(getStoredPinKey(userId), pin)
  }

  return {
    isSupported,
    isPlatformAuthenticatorAvailable,
    isEnabled,
    enroll,
    authenticate,
    disable,
    disableAll,
    updateStoredPin,
  }
}
